import Sidebar from "../components/Sidebar";
import React, { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { makeQueryString } from "../APIs/request";
import { usePaginationParams } from "../hooks/usePaginationParams";
import { useMe } from "../hooks/useMe";
import "../styles/list.scss";
import API from "../APIs/API";
import { ADMIN_LOCATION_LEVEL_USER_OPTIONS } from "../constants";
import { toast } from "react-toastify";

const Staff = ({ type }) => {
  const { admin_location_id } = useParams();
  const { gridParams, queryParams } = usePaginationParams();
  const [adminNoDesc, setAdminNoDesc] = useState("All");
  const [paypointDesc, setPaypointDesc] = useState("All");
  const { user: loggedUser } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const pageUserType = type === "Non-Teacher" ? "non-teachers" : "teachers";

  const { data: adminLocation } = useQuery(
    ["admin_location", admin_location_id],
    {
      queryFn: async () => {
        const result = await new API().getAdminLocationById(admin_location_id);
        if (result) return result["data"];
        return null;
      },
    }
  );

  const { data: accountNoDescList = [] } = useQuery(["account_no_desc_list"], {
    queryFn: async () => {
      const result = await new API().getAccountNoDescList(admin_location_id);
      if (result) {
        if (
          loggedUser.admin_location_level_user ===
          ADMIN_LOCATION_LEVEL_USER_OPTIONS.ACCOUNT_NO_DESC_USER
        ) {
          setAdminNoDesc(loggedUser.account_no_desc);
        }
        return ["All", ...result["data"]];
      }
      return ["All"];
    },
  });

  const { data: employees = [], isFetching } = useQuery(
    [
      "staff_employees",
      adminNoDesc,
      paypointDesc,
      makeQueryString(queryParams, { admin_location_id }),
    ],
    {
      queryFn: async () => {
        let search = {};
        if (adminNoDesc !== "All") search["account_no_desc"] = adminNoDesc;
        if (paypointDesc !== "All") search["paypoint_desc"] = paypointDesc;
        const queryString = makeQueryString(queryParams, search, {
          admin_location_id,
        });
        const result = await new API().getAllStaffs(queryString);
        return result ?? [];
      },
    },
    { keepPreviousData: true }
  );

  const { data: paypointDescList = [] } = useQuery(["paypoint_desc_list"], {
    queryFn: async () => {
      const result = await new API().getPaypointDescList(
        admin_location_id,
        adminNoDesc
      );
      if (result) {
        if (
          loggedUser.admin_location_level_user ===
          ADMIN_LOCATION_LEVEL_USER_OPTIONS.PAYPOINT_DESC_USER
        ) {
          setAdminNoDesc(loggedUser.paypoint_desc);
        }
        return ["All", ...result["data"]];
      }
      return ["All"];
    },
    enabled: adminNoDesc !== "All",
  });

  // Excel import mutation
  const importMutation = useMutation(
    (file) => new API().importExcelForStaff(admin_location_id, file),
    {
      onSuccess: (data) => {
        const count = data?.imported ?? 0;
        toast.success(`Excel imported successfully! ${count} record${count !== 1 ? "s" : ""} imported.`);
        setShowImportModal(false);
        setSelectedFile(null);
        queryClient.invalidateQueries("staff_employees");
      },
      onError: (err) => toast.error(err?.message || "Import failed. Please check the file and try again."),
    }
  );

  const handleImportSubmit = (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.warning("Please select an Excel file first.");
      return;
    }
    importMutation.mutate(selectedFile);
  };

  // Action column — View button
  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="cellAction">
          <div
            className="viewButton"
            onClick={() =>
              navigate(
                `/${pageUserType}/${admin_location_id}/staff/${row.id}`
              )
            }
          >
            View
          </div>
        </div>
      ),
    },
  ];

  const staffColumns = [
    {
      field: "establishment_status",
      headerName: "Establishment Status",
      width: 200,
      sortable: false,
      renderCell: ({ row }) => {
        const occupied = row.employee_no && row.position_attached !== false;
        return (
          <span style={{ fontWeight: 600, color: occupied ? "#1a7f3c" : "#b44a00" }}>
            {occupied ? "Occupied" : "Vacant"}
          </span>
        );
      },
    },
    { field: "position_no",     headerName: "Position No",     width: 180 },
    { field: "occup_pos_title", headerName: "Occup Pos Title", width: 220 },
    { field: "award",           headerName: "Award",           width: 120 },
    { field: "classification",  headerName: "Classification",  width: 160 },
    { field: "account_no_desc", headerName: "Account No Desc", width: 280 },
    { field: "paypoint_desc",   headerName: "Paypoint Desc",   width: 260 },
  ];

  return (
    <div className="wrapper">
      <div className="sidebar">
        <Sidebar />
      </div>
      <div className="content">
        <div className="list">
          <div className="listContainer">
            <div className="datatable">
              <Link to={`/${pageUserType}`} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br />
              <br />

              {/* Title row with Excel Import + Add New buttons */}
              <div className="datatableTitle">
                {`${adminLocation?.admin_desc ?? ''} Staff Establishment`}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="addBtn"
                    style={{ backgroundColor: "#4b4b9c" }}
                    onClick={() => setShowImportModal(true)}
                  >
                    Excel Import Position
                  </button>
                  <button
                    className="addBtn"
                    onClick={() =>
                      navigate(`/${pageUserType}/${admin_location_id}/staff/add`)
                    }
                  >
                    Add New Position
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="searchSection">
                <div className="row">
                  <div className="col col-sm-4">
                    <TextField
                      id="outlined-disabled"
                      label="Admin desc"
                      fullWidth
                      InputProps={{ readOnly: true }}
                      value={adminLocation?.admin_desc ?? ""}
                    />
                  </div>
                  <div className="col col-sm-4">
                    <Autocomplete
                      disablePortal
                      id="combo-box"
                      options={accountNoDescList}
                      fullWidth
                      disabled={
                        loggedUser.admin_location_level_user ===
                          ADMIN_LOCATION_LEVEL_USER_OPTIONS.ACCOUNT_NO_DESC_USER ||
                        loggedUser.admin_location_level_user ===
                          ADMIN_LOCATION_LEVEL_USER_OPTIONS.PAYPOINT_DESC_USER
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Account no desc" />
                      )}
                      onChange={(e, value) => setAdminNoDesc(value)}
                      disableClearable
                      value={adminNoDesc}
                    />
                  </div>
                  <div className="col col-sm-4">
                    <Autocomplete
                      hidden={adminNoDesc === "All"}
                      disablePortal
                      id="combo-box-paypoint"
                      options={paypointDescList}
                      fullWidth
                      disabled={
                        loggedUser.admin_location_level_user ===
                        ADMIN_LOCATION_LEVEL_USER_OPTIONS.PAYPOINT_DESC_USER
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Paypoint desc" />
                      )}
                      onChange={(e, value) => setPaypointDesc(value)}
                      disableClearable
                      value={paypointDesc}
                    />
                  </div>
                </div>
              </div>
              <br />

              <DataGrid
                className="datagrid"
                rows={employees?.rows || []}
                columns={actionColumn.concat(staffColumns)}
                rowCount={employees?.count ?? 0}
                loading={isFetching}
                {...gridParams}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Excel Import Modal */}
      {showImportModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1300,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowImportModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "32px 36px",
              minWidth: 420,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 8 }}>Excel Import — Staff</h3>
            <p style={{ color: "#555", fontSize: 13, marginBottom: 20 }}>
              Upload an Excel file (.xls / .xlsx) containing staff establishment
              data for <b>{adminLocation?.admin_desc ?? "this admin location"}</b>.
              The file must have the columns matching the standard format
              (Admin Location, Admin Desc, Employee No, Name Report, etc.).
            </p>

            <form onSubmit={handleImportSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  Select Excel File
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xls,.xlsx"
                  style={{ width: "100%" }}
                  onChange={(e) => setSelectedFile(e.target.files[0] || null)}
                />
                {selectedFile && (
                  <p style={{ color: "#333", fontSize: 12, marginTop: 6 }}>
                    Selected: <b>{selectedFile.name}</b>
                  </p>
                )}
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="app-button app-button-gray"
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedFile(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="app-button app-button-primary"
                  disabled={!selectedFile || importMutation.isLoading}
                >
                  {importMutation.isLoading ? "Importing…" : "Import"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staff;
